import type { Position, PositionCode, PositionWeights, SlotSpec } from '../types.ts';

/** Top of the position-weight scale: the role a player is picked for. */
export const WEIGHT_MAX = 5;
/** A position he can be asked to cover — the second step of the picker's cycle. */
export const WEIGHT_SECONDARY = 2;
/** What a position is worth when only the compatibility graph derives it. */
export const WEIGHT_DERIVED = 1;

/**
 * Every position, in the order the interface lists them.
 * `code` is internal; `localizedCode` and `localizedName` are what the user reads.
 */
export const POSITIONS: Position[] = [
  { code: 'GK', localizedCode: 'KL', localizedName: 'Kaleci' },
  { code: 'LB', localizedCode: 'SLB', localizedName: 'Sol Bek' },
  { code: 'LCB', localizedCode: 'SLS', localizedName: 'Sol Stoper' },
  { code: 'CB', localizedCode: 'STP', localizedName: 'Stoper' },
  { code: 'RCB', localizedCode: 'SĞS', localizedName: 'Sağ Stoper' },
  { code: 'RB', localizedCode: 'SĞB', localizedName: 'Sağ Bek' },
  { code: 'DM', localizedCode: 'DOS', localizedName: 'Ön Libero' },
  { code: 'CM', localizedCode: 'MO', localizedName: 'Merkez OS' },
  { code: 'AM', localizedCode: 'OOS', localizedName: 'Ofansif OS' },
  { code: 'LW', localizedCode: 'SLK', localizedName: 'Sol Kanat' },
  { code: 'RW', localizedCode: 'SĞK', localizedName: 'Sağ Kanat' },
  { code: 'SS', localizedCode: 'İF', localizedName: 'İkinci Forvet' },
  { code: 'ST', localizedCode: 'SNT', localizedName: 'Santrfor' },
];

export const POSITION_CODES: PositionCode[] = POSITIONS.map((p) => p.code);

const BY_CODE = new Map<PositionCode, Position>(POSITIONS.map((p) => [p.code, p]));

export function position(code: PositionCode): Position {
  const found = BY_CODE.get(code);
  if (!found) throw new Error('unknown position code: ' + code);
  return found;
}

/** Short label for a position, e.g. 'SĞB'. */
export function localizedCode(code: PositionCode): string {
  return position(code).localizedCode;
}

/** Full label for a position, e.g. 'Sağ Bek'. */
export function localizedName(code: PositionCode): string {
  return position(code).localizedName;
}

/**
 * The positions a weight map lists, heaviest first.
 *
 * Equal weights keep the order they were **written in**, which is why the roster lists a
 * player's positions in the order a coach would say them. The scale only has five steps,
 * so a player who is genuinely a 5 in four different roles is common — Kaan Ayhan is one
 * — and without this his primary position would fall out of an unrelated alphabet.
 *
 * `Object.keys` gives insertion order for non-integer string keys, and `Array.sort` is
 * stable, so authoring order survives the sort untouched.
 */
export function positionsByWeight(weights: PositionWeights): PositionCode[] {
  return (Object.keys(weights) as PositionCode[])
    .filter((code) => (weights[code] ?? 0) > 0)
    .sort((a, b) => (weights[b] ?? 0) - (weights[a] ?? 0));
}

/**
 * Interchangeable positions: these count as a perfect fit for one another.
 * The middle of a back three is open to either centre-back.
 */
export const EQUIVALENT: Partial<Record<PositionCode, PositionCode[]>> = {
  CB: ['LCB', 'RCB'],
  LCB: ['CB'],
  RCB: ['CB'],
};

/** Positions a player of a given position can also cover, at reduced suitability. */
export const COMPATIBLE: Record<PositionCode, PositionCode[]> = {
  GK: [],
  LB: ['LCB', 'LW', 'RB'],
  LCB: ['RCB', 'LB'],
  CB: ['DM'],
  RCB: ['LCB', 'RB'],
  RB: ['RCB', 'RW', 'LB'],
  DM: ['CM', 'CB'],
  CM: ['DM', 'AM'],
  AM: ['CM', 'LW', 'RW', 'SS', 'ST'],
  LW: ['LB', 'AM', 'SS', 'RW'],
  RW: ['RB', 'AM', 'SS', 'LW'],
  SS: ['ST', 'AM', 'LW', 'RW'],
  ST: ['SS', 'AM', 'LW', 'RW'],
};

/**
 * Rows a slot may be filled from when nobody in the squad is filed under its own
 * position. Ranked below an equivalent, so a natural İkinci Forvet always beats a
 * centre-forward borrowed from the striker row.
 *
 * This exists because `SS` is a role a squad can simply lack. It is not interchangeable
 * with `ST` — that is the whole point of separating them, and `suitability` keeps them
 * apart — but a 4-4-2's second forward has to be *someone*, and in practice that is the
 * second-choice striker dropping off.
 */
export const SLOT_FALLBACK: Partial<Record<PositionCode, PositionCode[]>> = {
  SS: ['ST', 'AM'],
};

/** Each slot is [position, left %, top %] on the pitch box. */
export const FORMATIONS = {
  '4-2-3-1': [
    ['GK', 50, 93],
    ['LB', 11, 75],
    ['LCB', 35, 80],
    ['RCB', 65, 80],
    ['RB', 89, 75],
    ['CM', 35, 58],
    ['CM', 65, 58],
    ['LW', 14, 37],
    ['AM', 50, 40],
    ['RW', 86, 37],
    ['ST', 50, 15],
  ],
  '4-3-3': [
    ['GK', 50, 93],
    ['LB', 11, 75],
    ['LCB', 35, 80],
    ['RCB', 65, 80],
    ['RB', 89, 75],
    ['DM', 50, 62],
    ['CM', 27, 49],
    ['CM', 73, 49],
    ['LW', 14, 27],
    ['ST', 50, 15],
    ['RW', 86, 27],
  ],
  '4-4-2': [
    ['GK', 50, 93],
    ['LB', 11, 75],
    ['LCB', 35, 80],
    ['RCB', 65, 80],
    ['RB', 89, 75],
    ['LW', 11, 52],
    ['CM', 38, 55],
    ['CM', 62, 55],
    ['RW', 89, 52],
    ['ST', 37, 15],
    // The İkinci Forvet plays *off* the centre-forward, so he sits deeper as well as
    // wider. Putting him level with the nine would have drawn twin strikers again and
    // said nothing the old two-ST pair did not.
    ['SS', 63, 28],
  ],
  '4-6-0': [
    ['GK', 50, 93],
    ['LB', 11, 75],
    ['LCB', 35, 80],
    ['RCB', 65, 80],
    ['RB', 89, 75],
    ['DM', 35, 62],
    ['CM', 65, 62],
    ['LW', 11, 42],
    ['AM', 38, 34],
    ['AM', 62, 34],
    ['RW', 89, 42],
  ],
  '4-1-4-1': [
    ['GK', 50, 93],
    ['LB', 11, 75],
    ['LCB', 35, 80],
    ['RCB', 65, 80],
    ['RB', 89, 75],
    ['DM', 50, 63],
    ['LW', 12, 44],
    ['CM', 38, 45],
    ['CM', 62, 45],
    ['RW', 88, 44],
    ['ST', 50, 16],
  ],
  '4-2-2-2': [
    ['GK', 50, 93],
    ['LB', 11, 75],
    ['LCB', 35, 80],
    ['RCB', 65, 80],
    ['RB', 89, 75],
    ['DM', 35, 60],
    ['DM', 65, 60],
    ['AM', 24, 39],
    ['AM', 76, 39],
    ['ST', 37, 17],
    ['ST', 63, 17],
  ],
  '4-3-1-2': [
    ['GK', 50, 93],
    ['LB', 11, 75],
    ['LCB', 35, 80],
    ['RCB', 65, 80],
    ['RB', 89, 75],
    ['CM', 27, 57],
    ['DM', 50, 63],
    ['CM', 73, 57],
    ['AM', 50, 37],
    ['ST', 37, 17],
    ['ST', 63, 17],
  ],
  '3-5-2': [
    ['GK', 50, 93],
    ['LCB', 25, 80],
    ['CB', 50, 83],
    ['RCB', 75, 80],
    ['LW', 9, 54],
    ['CM', 33, 57],
    ['DM', 50, 64],
    ['CM', 67, 57],
    ['RW', 91, 54],
    ['ST', 37, 19],
    ['ST', 63, 19],
  ],
  '3-4-3': [
    ['GK', 50, 93],
    ['LCB', 25, 80],
    ['CB', 50, 83],
    ['RCB', 75, 80],
    ['LW', 10, 55],
    ['CM', 37, 58],
    ['CM', 63, 58],
    ['RW', 90, 55],
    ['LW', 18, 25],
    ['ST', 50, 15],
    ['RW', 82, 25],
  ],
  '3-4-2-1': [
    ['GK', 50, 93],
    ['LCB', 25, 80],
    ['CB', 50, 83],
    ['RCB', 75, 80],
    ['LW', 10, 57],
    ['CM', 37, 60],
    ['CM', 63, 60],
    ['RW', 90, 57],
    ['AM', 30, 36],
    ['AM', 70, 36],
    ['ST', 50, 15],
  ],
  '5-3-2': [
    ['GK', 50, 93],
    ['LB', 8, 68],
    ['LCB', 28, 81],
    ['CB', 50, 84],
    ['RCB', 72, 81],
    ['RB', 92, 68],
    ['CM', 30, 51],
    ['DM', 50, 58],
    ['CM', 70, 51],
    ['ST', 37, 19],
    ['ST', 63, 19],
  ],
} as const satisfies Record<string, readonly SlotSpec[]>;

export type SlotSide = 'left' | 'right' | 'center';

/**
 * Which flank a slot sits on, read from its horizontal position. Slots on the centre
 * line are neither, so a flank preference never applies to them.
 */
export function slotSide(slot: SlotSpec): SlotSide {
  if (slot[1] < 50) return 'left';
  if (slot[1] > 50) return 'right';
  return 'center';
}

export type FormationCode = keyof typeof FORMATIONS;

export const FORMATION_CODES = Object.keys(FORMATIONS) as FormationCode[];
